import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  useLegalHolds,
  usePlaceLegalHold,
  usePurgeDryRun,
  useReleaseLegalHold,
  useRetentionPolicies,
  useUpsertRetentionPolicy,
} from "@/api/queries/retention";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import {
  PURGEABLE_DATA_CLASSES,
  dataClassLabel,
  type RetentionDataClass,
} from "@/lib/retention";

const selectClass =
  "h-10 rounded-md border border-input bg-background px-3 text-sm";

export function RetentionPage() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;

  if (!isRespaiAdmin(identity?.role)) {
    return (
      <p className="text-sm text-destructive">
        Retention administration is restricted to RespAI platform administrators.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Retention &amp; legal hold</h1>
        <p className="text-sm text-muted-foreground">
          Per-data-class retention for{" "}
          <span className="font-medium text-foreground">
            {me.data?.department?.name ?? "your department"}
          </span>
          . Purge is preview-only. Destructive deletion stays disabled until
          backups are restore-tested.
        </p>
      </header>

      <PoliciesCard tz={tz} />
      <LegalHoldsCard tz={tz} />
      <DryRunCard tz={tz} />
    </div>
  );
}

function PoliciesCard({ tz }: { tz?: string }) {
  const policies = useRetentionPolicies();
  const upsert = useUpsertRetentionPolicy();
  const [dataClass, setDataClass] = useState<RetentionDataClass>("gas_reading");
  const [days, setDays] = useState("90");
  const [enabled, setEnabled] = useState(true);

  const existing = useMemo(
    () => policies.data?.find((p) => p.data_class === dataClass),
    [policies.data, dataClass],
  );

  const onSelect = (value: RetentionDataClass) => {
    setDataClass(value);
    const match = policies.data?.find((p) => p.data_class === value);
    if (match) {
      setDays(String(match.retention_days));
      setEnabled(match.enabled);
    }
  };

  const daysNum = Number(days);
  const valid = Number.isInteger(daysNum) && daysNum > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Retention policies</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="mb-1">Data class</div>
            <select
              className={selectClass}
              value={dataClass}
              onChange={(e) => onSelect(e.target.value as RetentionDataClass)}
            >
              {PURGEABLE_DATA_CLASSES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="mb-1">Retention (days)</div>
            <Input
              type="number"
              min={1}
              className="w-32"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
          <Button
            disabled={!valid || upsert.isPending}
            onClick={() =>
              upsert.mutate({
                data_class: dataClass,
                retention_days: daysNum,
                enabled,
              })
            }
          >
            {upsert.isPending
              ? "Saving…"
              : existing
                ? "Update policy"
                : "Create policy"}
          </Button>
        </div>
        {upsert.isError ? (
          <p className="text-xs text-destructive">
            {upsert.error instanceof Error
              ? upsert.error.message
              : "Could not save policy."}
          </p>
        ) : null}

        {policies.isLoading ? (
          <Skeleton className="h-24" />
        ) : policies.data && policies.data.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Data class</Th>
                <Th>Retention (days)</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
              </Tr>
            </Thead>
            <Tbody>
              {policies.data.map((p) => (
                <Tr key={p.id}>
                  <Td className="font-medium">{dataClassLabel(p.data_class)}</Td>
                  <Td className="tabular-nums">{p.retention_days}</Td>
                  <Td>
                    <Badge variant={p.enabled ? "success" : "outline"}>
                      {p.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </Td>
                  <Td>{formatDateTime(p.updated_at, tz)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No retention policies configured yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LegalHoldsCard({ tz }: { tz?: string }) {
  const holds = useLegalHolds();
  const place = usePlaceLegalHold();
  const release = useReleaseLegalHold();
  const [incidentId, setIncidentId] = useState("");
  const [reason, setReason] = useState("");

  const onPlace = () => {
    place.mutate(
      {
        incident_id: incidentId.trim() || null,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          setIncidentId("");
          setReason("");
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Legal holds</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="mb-1">Incident ID (optional)</div>
            <Input
              className="w-72"
              placeholder="Blank = department-wide hold"
              value={incidentId}
              onChange={(e) => setIncidentId(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <div className="mb-1">Reason</div>
            <Input
              className="w-72"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Button
            disabled={!reason.trim() || place.isPending}
            onClick={onPlace}
          >
            {place.isPending ? "Placing…" : "Place hold"}
          </Button>
        </div>
        {place.isError ? (
          <p className="text-xs text-destructive">
            {place.error instanceof Error
              ? place.error.message
              : "Could not place hold."}
          </p>
        ) : null}

        {holds.isLoading ? (
          <Skeleton className="h-24" />
        ) : holds.data && holds.data.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Scope</Th>
                <Th>Reason</Th>
                <Th>Placed</Th>
                <Th>Status</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {holds.data.map((h) => {
                const active = h.released_at == null;
                return (
                  <Tr key={h.id}>
                    <Td>
                      {h.incident_id ? (
                        <span className="font-mono text-xs">
                          {h.incident_id.slice(0, 8)}
                        </span>
                      ) : (
                        <Badge variant="secondary">Department-wide</Badge>
                      )}
                    </Td>
                    <Td>{h.reason}</Td>
                    <Td>{formatDateTime(h.placed_at, tz)}</Td>
                    <Td>
                      <Badge variant={active ? "warning" : "outline"}>
                        {active
                          ? "Active"
                          : `Released ${formatDateTime(h.released_at, tz)}`}
                      </Badge>
                    </Td>
                    <Td>
                      {active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={release.isPending}
                          onClick={() => release.mutate(h.id)}
                        >
                          Release
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No legal holds on record.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function DryRunCard({ tz }: { tz?: string }) {
  const dryRun = usePurgeDryRun();
  const [dataClass, setDataClass] = useState<RetentionDataClass>("gas_reading");
  const preview = dryRun.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Purge preview (dry run)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          Dry run only. This counts what would be purged and writes an audit
          record. It never deletes. Legal-held rows are always excluded.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="mb-1">Data class</div>
            <select
              className={selectClass}
              value={dataClass}
              onChange={(e) => setDataClass(e.target.value as RetentionDataClass)}
            >
              {PURGEABLE_DATA_CLASSES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            disabled={dryRun.isPending}
            onClick={() => dryRun.mutate(dataClass)}
          >
            {dryRun.isPending ? "Previewing…" : "Preview purge"}
          </Button>
        </div>
        {dryRun.isError ? (
          <p className="text-xs text-destructive">
            {dryRun.error instanceof Error
              ? dryRun.error.message
              : "Dry run failed (a retention policy must exist and be enabled)."}
          </p>
        ) : null}

        {preview ? (
          <div className="space-y-3 rounded-md border border-border p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Cutoff" value={formatDateTime(preview.cutoff_at, tz)} />
              <Stat label="Candidates" value={String(preview.candidate_count)} />
              <Stat
                label="Held back"
                value={String(preview.held_back_count)}
              />
              <Stat label="Purgeable" value={String(preview.purgeable_count)} />
            </div>
            <p className="text-xs text-muted-foreground">
              {dataClassLabel(preview.data_class)} · retention{" "}
              {preview.retention_days} days · executed:{" "}
              {preview.executed ? "yes" : "no (dry run)"}
            </p>
            {preview.warnings.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {preview.warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
